/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  ConditionFunctionConfig,
  ConditionsConfig_ConditionConfig,
  Member,
  Team,
  OutputDescription,
  Editor,
} from "@smartsuite/automation-engine-api";
import { ConditionsLogicOperator } from "@smartsuite/automation-engine-api";
import * as R from "remeda";
import { valueConfigsVToDescriptionString } from "./helper";
import type { PhraseDirectory } from "./helper";
import type { TriggerCandidate } from "../../types/candidate";
import { getOutputDescriptions } from "../../utils/conditions-coverter.hook";
import type { CandidateStore } from "../../stores/automation/candidate.store";
import { resolveReferencedConditionField } from "../../containers/AutomationForm/Sections/TriggerSection/ConditionsForm/ConditionItem/left-source/resolve-referenced-condition-field";

type ConditionsFormatters = Partial<
  Record<ConditionsLogicOperator, ConditionFormatter>
>;

type ConditionFormatter = (
  trigger: TriggerCandidate,
  members: Member[],
  teams: Team[],
  /* eslint-disable  @typescript-eslint/no-explicit-any */
) => string | { [key: string]: any };

export const conditionsText = (
  func: ConditionFunctionConfig,
  conditionFieldLabel: string,
  outputsList: OutputDescription[],
  editor: Editor,
  /** Workspace members/teams, needed to name a member/team comparison value
   * (STR-14493). Omitted -> those values keep rendering empty. */
  directory?: PhraseDirectory,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): string | { [key: string]: any } => {
  switch (func.function.oneofKind) {
    case "isEquals": {
      if (func.function.isEquals.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.isEquals.right.values,
        outputsList,
        directory,
      );

      if (editor.editor.oneofKind === "number") {
        return {
          key: "phrase-builder.is-equals",
          data: { conditionFieldLabel, right },
        };
      }

      if (
        (editor.editor.oneofKind === "select" &&
          editor.editor.select.multiple) ||
        editor.editor.oneofKind === "member"
      ) {
        return {
          key: "phrase-builder.is-exactly",
          data: { conditionFieldLabel, right },
        };
      }
      return {
        key: "phrase-builder.is",
        data: { conditionFieldLabel, right },
      };
    }

    case "isNotEquals": {
      if (func.function.isNotEquals.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.isNotEquals.right.values,
        outputsList,
        directory,
      );
      if (editor.editor.oneofKind === "number") {
        return {
          key: "phrase-builder.is-not-equals",
          data: { conditionFieldLabel, right },
        };
      }
      return {
        key: "phrase-builder.is-not",
        data: { conditionFieldLabel, right },
      };
    }

    case "isEmpty": {
      return {
        key: "phrase-builder.is-empty",
        data: { conditionFieldLabel },
      };
    }

    case "isNotEmpty": {
      return {
        key: "phrase-builder.is-not-empty",
        data: { conditionFieldLabel },
      };
    }

    case "contains": {
      if (func.function.contains.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.contains.right.values,
        outputsList,
        directory,
      );
      return {
        key: "phrase-builder.contains",
        data: { conditionFieldLabel, right },
      };
    }

    case "notContains": {
      if (func.function.notContains.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.notContains.right.values,
        outputsList,
        directory,
      );
      return {
        key: "phrase-builder.not-contains",
        data: { conditionFieldLabel, right },
      };
    }

    case "changes": {
      if (func.function.changes.before === undefined) {
        return "";
      }
      if (func.function.changes.after === undefined) {
        return "";
      }
      const fromChangeCondition = conditionsText(
        func.function.changes.before,
        conditionFieldLabel,
        outputsList,
        editor,
        directory,
      );
      const toChangeCondition = conditionsText(
        func.function.changes.after,
        conditionFieldLabel,
        outputsList,
        editor,
        directory,
      );
      return {
        key: "phrase-builder.changes",
        data: {
          conditionFieldLabel,
          fromChangeCondition,
          toChangeCondition,
        },
      };
    }

    case "lessThan": {
      if (func.function.lessThan.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.lessThan.right.values,
        outputsList,
        directory,
      );
      if (editor.editor.oneofKind === "number") {
        return {
          key: "phrase-builder.less-than",
          data: { conditionFieldLabel, right },
        };
      }
      return {
        key: "phrase-builder.is-before",
        data: { conditionFieldLabel, right },
      };
    }

    case "greaterThan": {
      if (func.function.greaterThan.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.greaterThan.right.values,
        outputsList,
        directory,
      );
      if (editor.editor.oneofKind === "number") {
        return {
          key: "phrase-builder.is-greater-than",
          data: { conditionFieldLabel, right },
        };
      }
      return {
        key: "phrase-builder.is-after",
        data: { conditionFieldLabel, right },
      };
    }

    case "lessThanOrEquals": {
      if (func.function.lessThanOrEquals.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.lessThanOrEquals.right.values,
        outputsList,
        directory,
      );
      if (editor.editor.oneofKind === "number") {
        return {
          key: "phrase-builder.less-than-or-equals",
          data: { conditionFieldLabel, right },
        };
      }
      return {
        key: "phrase-builder.is-on-or-before",
        data: { conditionFieldLabel, right },
      };
    }

    case "greaterThanOrEquals": {
      if (func.function.greaterThanOrEquals.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.greaterThanOrEquals.right.values,
        outputsList,
        directory,
      );
      if (editor.editor.oneofKind === "number") {
        return {
          key: "phrase-builder.is-greater-or-equal-than",
          data: { conditionFieldLabel, right },
        };
      }
      return {
        key: "phrase-builder.is-on-or-after",
        data: { conditionFieldLabel, right },
      };
    }

    case "hasAnyOf": {
      if (func.function.hasAnyOf.haystack?.values === undefined) {
        return "";
      }
      const haystack = valueConfigsVToDescriptionString(
        func.function.hasAnyOf.haystack.values,
        outputsList,
        directory,
      );
      return {
        key: "phrase-builder.has-any-of",
        data: { conditionFieldLabel, haystack },
      };
    }

    case "hasAllOf": {
      if (func.function.hasAllOf.haystack?.values === undefined) {
        return "";
      }
      const haystack = valueConfigsVToDescriptionString(
        func.function.hasAllOf.haystack.values,
        outputsList,
        directory,
      );
      return {
        key: "phrase-builder.has-all-of",
        data: { conditionFieldLabel, haystack },
      };
    }

    case "hasNoneOf": {
      if (func.function.hasNoneOf.haystack?.values === undefined) {
        return "";
      }
      const haystack = valueConfigsVToDescriptionString(
        func.function.hasNoneOf.haystack.values,
        outputsList,
        directory,
      );
      return {
        key: "phrase-builder.has-none-of",
        data: { conditionFieldLabel, haystack },
      };
    }

    case "isAnyOf": {
      if (func.function.isAnyOf.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.isAnyOf.right.values,
        outputsList,
        directory,
      );
      return {
        key: "phrase-builder.is-any-of",
        data: { conditionFieldLabel, right },
      };
    }

    case "isNoneOf": {
      if (func.function.isNoneOf.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.isNoneOf.right.values,
        outputsList,
        directory,
      );
      return {
        key: "phrase-builder.is-none-of",
        data: { conditionFieldLabel, right },
      };
    }

    case "isAny": {
      return {
        key: "phrase-builder.is-any",
        data: { conditionFieldLabel },
      };
    }

    case "allItemsResolved": {
      return {
        key: "phrase-builder.all-items-resolved",
        data: { conditionFieldLabel },
      };
    }
    case "notAllItemsResolved": {
      return {
        key: "phrase-builder.not-all-items-resolved",
        data: { conditionFieldLabel },
      };
    }
    case "percentCompleteLessThan": {
      if (func.function.percentCompleteLessThan.right?.values === undefined) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.percentCompleteLessThan.right.values,
        outputsList,
        directory,
      );
      return {
        key: "phrase-builder.percent-complete-less-than",
        data: { conditionFieldLabel, right },
      };
    }
    case "percentCompleteGreaterThan": {
      if (
        func.function.percentCompleteGreaterThan.right?.values === undefined
      ) {
        return "";
      }
      const right = valueConfigsVToDescriptionString(
        func.function.percentCompleteGreaterThan.right.values,
        outputsList,
        directory,
      );
      return {
        key: "phrase-builder.percent-complete-greater-than",
        data: { conditionFieldLabel, right },
      };
    }
    case "isComplete": {
      return {
        key: "phrase-builder.is-complete",
        data: { conditionFieldLabel },
      };
    }
    case "isNotComplete": {
      return {
        key: "phrase-builder.is-not-complete",
        data: { conditionFieldLabel },
      };
    }
    default:
      return "";
  }
};

const getConditionTexts = (
  trigger: TriggerCandidate,
  directory?: PhraseDirectory,
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): Array<string | { [key: string]: any }> | undefined =>
  trigger.conditions?.group
    .map((group) => {
      if (group.group.oneofKind === "condition") {
        if (group.group.condition.function?.function.oneofKind !== undefined) {
          const condition: ConditionsConfig_ConditionConfig =
            group.group.condition;
          // Reference-mode branch conditions store the left side as an
          // outputReference; Trigger/Find conditions still use conditionFieldId.
          // This site only has the trigger (no actions list), so it can resolve
          // a trigger-sourced reference but not an action-sourced one -- the
          // resolver's own empty allActions makes that a safe no-crash miss
          // rather than a throw.
          const conditionField =
            condition.outputReference !== undefined
              ? resolveReferencedConditionField(
                  {
                    self: { trigger },
                    allActions: [],
                  } as unknown as CandidateStore,
                  condition.outputReference,
                )?.fieldDescription
              : trigger.description.conditionFields.find(
                  (description) =>
                    description.conditionFieldId === condition.conditionFieldId,
                );
          const outputs: OutputDescription[] = getOutputDescriptions(trigger);

          if (R.isTruthy(conditionField) && R.isTruthy(condition.function)) {
            return conditionsText(
              condition.function,
              conditionField.label,
              outputs,
              conditionField.editor!,
              directory,
            );
          }
        }
      }
      return "";
    })
    .filter((text) => R.isTruthy(text));

const operatorAndFormatter = (
  trigger: TriggerCandidate,
  members: Member[],
  teams: Team[],
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): string | { [key: string]: any } => {
  const conditionsTexts = getConditionTexts(trigger, { members, teams });
  const operatorAndText = conditionsTexts?.map((conditionText, index) => {
    if (index === 0) {
      return { key: "phrase-builder.and-only-if", data: { conditionText } };
    }
    return { key: "phrase-builder.and-condition", data: { conditionText } };
  });

  return operatorAndText ?? "";
};

const operatorOrFormatter = (
  trigger: TriggerCandidate,
  members: Member[],
  teams: Team[],
): string | { [key: string]: any } => {
  const conditionsTexts = getConditionTexts(trigger, { members, teams });
  const operatorOrText = conditionsTexts?.map((conditionText, index) => {
    if (conditionsTexts.length === 1) {
      return { key: "phrase-builder.and-only-if", data: { conditionText } };
    }
    if (index === 0) {
      return { key: "phrase-builder.and-either", data: { conditionText } };
    }
    return { key: "phrase-builder.or-condition", data: { conditionText } };
  });

  return operatorOrText ?? "";
};

export const conditionsFormatters: ConditionsFormatters = {
  [ConditionsLogicOperator.OPERATOR_AND]: operatorAndFormatter,
  [ConditionsLogicOperator.OPERATOR_OR]: operatorOrFormatter,
};
